import { useState, useCallback, useEffect, useRef } from 'react';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3000';

/**
 * Sandbox status states
 */
export const SANDBOX_STATUS = {
  UNKNOWN: 'unknown',
  CHECKING: 'checking',
  NOT_FOUND: 'not_found',
  INITIALIZING: 'initializing',
  SYNCING: 'syncing',
  GENERATING: 'generating',
  READY: 'ready',
  STALE: 'stale',
  ERROR: 'error',
};

/**
 * Dev server status states
 */
export const DEV_SERVER_STATUS = {
  STOPPED: 'stopped',
  STARTING: 'starting',
  RUNNING: 'running',
  STOPPING: 'stopping',
  ERROR: 'error',
};

/**
 * Hook for managing per-brand sandbox lifecycle
 * 
 * @param {Object} config
 * @param {string} config.brandId - Brand UUID
 * @param {string} config.brandSlug - Brand slug for path resolution
 * @param {boolean} config.autoInit - Auto-initialize sandbox if not found (default: true)
 * @returns {Object} Sandbox state and controls
 */
export function useBrandSandbox({ brandId, brandSlug, autoInit = true, autoStartDevServer = true } = {}) {
  const [status, setStatus] = useState(SANDBOX_STATUS.UNKNOWN);
  const [sandboxInfo, setSandboxInfo] = useState(null);
  const [error, setError] = useState(null);
  const [lastSync, setLastSync] = useState(null);
  const [lastGenerate, setLastGenerate] = useState(null);
  
  // Dev server state
  const [devServerStatus, setDevServerStatus] = useState(DEV_SERVER_STATUS.STOPPED);
  const [devServerUrl, setDevServerUrl] = useState(null);
  const [devServerPort, setDevServerPort] = useState(null);
  const [devServerError, setDevServerError] = useState(null);
  
  const initAttempted = useRef(false);
  const devServerStartAttempted = useRef(false);

  /**
   * Check sandbox status
   */
  const checkStatus = useCallback(async () => {
    if (!brandId) return null;
    
    setStatus(SANDBOX_STATUS.CHECKING);
    setError(null);
    
    try {
      const response = await fetch(`${API_BASE}/api/brands/${brandId}/sandbox`, {
        credentials: 'include',
      });
      
      // Handle 404 specially
      if (response.status === 404) {
        setStatus(SANDBOX_STATUS.NOT_FOUND);
        setSandboxInfo(null);
        return { exists: false };
      }
      
      // Read body once
      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        data = { error: text || 'Unknown error' };
      }
      
      if (!response.ok) {
        throw new Error(data.error || `HTTP ${response.status}`);
      }
      
      setSandboxInfo(data);
      
      if (data.exists) {
        // Check if stale (specs newer than last sync)
        if (data.isStale) {
          setStatus(SANDBOX_STATUS.STALE);
        } else {
          setStatus(SANDBOX_STATUS.READY);
        }
      } else {
        setStatus(SANDBOX_STATUS.NOT_FOUND);
      }
      
      return data;
    } catch (err) {
      console.error('[Sandbox] Check status error:', err);
      setError(err.message);
      setStatus(SANDBOX_STATUS.ERROR);
      return null;
    }
  }, [brandId]);

  /**
   * Initialize sandbox
   */
  const initSandbox = useCallback(async (options = {}) => {
    if (!brandId) return null;
    
    setStatus(SANDBOX_STATUS.INITIALIZING);
    setError(null);
    
    try {
      const response = await fetch(`${API_BASE}/api/brands/${brandId}/sandbox`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          template: options.template || 'nextjs-site',
          initGit: options.initGit !== false,
        }),
      });
      
      // Read body once
      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        data = { error: text || 'Unknown error' };
      }
      
      if (!response.ok) {
        // 409 = already exists, which is fine
        if (response.status === 409 && data.info) {
          setSandboxInfo(data.info);
          setStatus(SANDBOX_STATUS.READY);
          return data.info;
        }
        throw new Error(data.error || `HTTP ${response.status}`);
      }
      
      setSandboxInfo(data);
      setStatus(SANDBOX_STATUS.READY);
      
      return data;
    } catch (err) {
      console.error('[Sandbox] Init error:', err);
      setError(err.message);
      setStatus(SANDBOX_STATUS.ERROR);
      return null;
    }
  }, [brandId]);

  /**
   * Sync specs from database to sandbox
   */
  const syncSpecs = useCallback(async () => {
    if (!brandId) return null;
    
    setStatus(SANDBOX_STATUS.SYNCING);
    setError(null);
    
    try {
      const response = await fetch(`${API_BASE}/api/brands/${brandId}/sandbox/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      
      // Read body once
      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        data = { error: text || 'Unknown error' };
      }
      
      if (!response.ok) {
        throw new Error(data.error || `HTTP ${response.status}`);
      }
      
      setLastSync(new Date().toISOString());
      setStatus(SANDBOX_STATUS.READY);
      
      // Refresh sandbox info
      await checkStatus();
      
      return data;
    } catch (err) {
      console.error('[Sandbox] Sync specs error:', err);
      setError(err.message);
      setStatus(SANDBOX_STATUS.ERROR);
      return null;
    }
  }, [brandId, checkStatus]);

  /**
   * Trigger site generation
   */
  const regenerate = useCallback(async (options = {}) => {
    if (!brandId) return null;
    
    setStatus(SANDBOX_STATUS.GENERATING);
    setError(null);
    
    try {
      const response = await fetch(`${API_BASE}/api/brands/${brandId}/sandbox/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          target: options.target || 'site',
          force: options.force || false,
        }),
      });
      
      // Read body once
      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        data = { error: text || 'Unknown error' };
      }
      
      if (!response.ok) {
        throw new Error(data.error || `HTTP ${response.status}`);
      }
      
      setLastGenerate(new Date().toISOString());
      setStatus(SANDBOX_STATUS.READY);
      
      // Refresh sandbox info
      await checkStatus();
      
      return data;
    } catch (err) {
      console.error('[Sandbox] Regenerate error:', err);
      setError(err.message);
      setStatus(SANDBOX_STATUS.ERROR);
      return null;
    }
  }, [brandId, checkStatus]);

  /**
   * Sync sandbox spec to database (for BrandOn)
   * This copies clarification_templates and other spec data from sandbox to the brand's profile_spec
   */
  const syncSpecToDatabase = useCallback(async () => {
    if (!brandId) return null;
    
    try {
      console.log(`[Sandbox] Syncing spec to database for brand ${brandSlug}...`);
      
      const response = await fetch(`${API_BASE}/api/brands/${brandId}/sandbox`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      
      const data = await response.json().catch(() => ({}));
      
      if (!response.ok) {
        throw new Error(data.error || `HTTP ${response.status}`);
      }
      
      console.log(`[Sandbox] Spec synced to database:`, data);
      return data;
    } catch (err) {
      console.error('[Sandbox] Sync spec to database error:', err);
      throw err;
    }
  }, [brandId, brandSlug]);

  /**
   * Reset sandbox (delete and recreate)
   */
  const resetSandbox = useCallback(async () => {
    if (!brandId) return null;
    
    setStatus(SANDBOX_STATUS.INITIALIZING);
    setError(null);
    
    try {
      // Stop dev server first if running
      if (devServerStatus === DEV_SERVER_STATUS.RUNNING) {
        await stopDevServer();
      }
      
      // Delete existing sandbox
      await fetch(`${API_BASE}/api/brands/${brandId}/sandbox`, {
        method: 'DELETE',
        credentials: 'include',
      });
      
      // Recreate
      const result = await initSandbox();
      
      // Sync specs
      if (result) {
        await syncSpecs();
      }
      
      return result;
    } catch (err) {
      setError(err.message);
      setStatus(SANDBOX_STATUS.ERROR);
      return null;
    }
  }, [brandId, initSandbox, syncSpecs, devServerStatus]);

  /**
   * Start the Next.js dev server for this brand
   */
  const startDevServer = useCallback(async () => {
    if (!brandId) return null;
    
    // Check if already running
    if (devServerStatus === DEV_SERVER_STATUS.RUNNING) {
      console.log('[DevServer] Already running');
      return { url: devServerUrl, port: devServerPort };
    }
    
    setDevServerStatus(DEV_SERVER_STATUS.STARTING);
    setDevServerError(null);
    
    try {
      console.log(`[DevServer] Starting for brand ${brandSlug}...`);
      
      const response = await fetch(`${API_BASE}/api/brands/${brandId}/sandbox/dev-server`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      
      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        data = { error: text || 'Unknown error' };
      }
      
      if (!response.ok) {
        throw new Error(data.error || `HTTP ${response.status}`);
      }
      
      if (data.status === 'running') {
        setDevServerStatus(DEV_SERVER_STATUS.RUNNING);
        setDevServerUrl(data.url);
        setDevServerPort(data.port);
        console.log(`[DevServer] Running at ${data.url}`);
        return data;
      } else if (data.status === 'error') {
        throw new Error(data.error || 'Dev server failed to start');
      } else {
        // Still starting, poll for status
        setDevServerStatus(DEV_SERVER_STATUS.STARTING);
        return data;
      }
    } catch (err) {
      console.error('[DevServer] Start error:', err);
      setDevServerError(err.message);
      setDevServerStatus(DEV_SERVER_STATUS.ERROR);
      return null;
    }
  }, [brandId, brandSlug, devServerStatus, devServerUrl, devServerPort]);

  /**
   * Stop the dev server
   */
  const stopDevServer = useCallback(async () => {
    if (!brandId) return null;
    
    setDevServerStatus(DEV_SERVER_STATUS.STOPPING);
    
    try {
      const response = await fetch(`${API_BASE}/api/brands/${brandId}/sandbox/dev-server`, {
        method: 'DELETE',
        credentials: 'include',
      });
      
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        console.warn('[DevServer] Stop warning:', data.error);
      }
      
      setDevServerStatus(DEV_SERVER_STATUS.STOPPED);
      setDevServerUrl(null);
      setDevServerPort(null);
      console.log('[DevServer] Stopped');
      
      return { stopped: true };
    } catch (err) {
      console.error('[DevServer] Stop error:', err);
      // Still mark as stopped since we can't verify
      setDevServerStatus(DEV_SERVER_STATUS.STOPPED);
      return null;
    }
  }, [brandId]);

  /**
   * Restart the dev server (stop, clean .next, start fresh)
   * Useful after branch operations
   */
  const restartDevServer = useCallback(async () => {
    if (!brandId) return null;
    
    setDevServerStatus(DEV_SERVER_STATUS.STARTING);
    setDevServerError(null);
    
    try {
      console.log(`[DevServer] Restarting for brand ${brandSlug}...`);
      
      const response = await fetch(`${API_BASE}/api/brands/${brandId}/sandbox/dev-server`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      
      const data = await response.json().catch(() => ({}));
      
      if (!response.ok) {
        throw new Error(data.error || `HTTP ${response.status}`);
      }
      
      if (data.status === 'running') {
        setDevServerStatus(DEV_SERVER_STATUS.RUNNING);
        setDevServerUrl(data.url);
        setDevServerPort(data.port);
        console.log(`[DevServer] Restarted at ${data.url}`);
        return data;
      } else {
        setDevServerStatus(DEV_SERVER_STATUS.STARTING);
        return data;
      }
    } catch (err) {
      console.error('[DevServer] Restart error:', err);
      setDevServerError(err.message);
      setDevServerStatus(DEV_SERVER_STATUS.ERROR);
      return null;
    }
  }, [brandId, brandSlug]);

  /**
   * Check dev server status
   */
  const checkDevServerStatus = useCallback(async () => {
    if (!brandId) return null;
    
    try {
      const response = await fetch(`${API_BASE}/api/brands/${brandId}/sandbox/dev-server`, {
        credentials: 'include',
      });
      
      if (!response.ok) {
        setDevServerStatus(DEV_SERVER_STATUS.STOPPED);
        return null;
      }
      
      const data = await response.json();
      
      if (data.running) {
        setDevServerStatus(DEV_SERVER_STATUS.RUNNING);
        setDevServerUrl(data.url);
        setDevServerPort(data.port);
      } else {
        setDevServerStatus(DEV_SERVER_STATUS.STOPPED);
        setDevServerUrl(null);
        setDevServerPort(null);
      }
      
      return data;
    } catch (err) {
      console.error('[DevServer] Status check error:', err);
      return null;
    }
  }, [brandId]);

  // Branch state
  const [currentBranch, setCurrentBranch] = useState('main');
  const [branches, setBranches] = useState([]);
  const [branchLoading, setBranchLoading] = useState(false);
  const [stashes, setStashes] = useState([]);
  const [hasStashes, setHasStashes] = useState(false);

  /**
   * Fetch all branches and stashes
   */
  const fetchBranches = useCallback(async () => {
    if (!brandId) return null;
    
    setBranchLoading(true);
    
    try {
      const response = await fetch(`${API_BASE}/api/brands/${brandId}/sandbox/git?action=branches`, {
        credentials: 'include',
      });
      
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || `HTTP ${response.status}`);
      }
      
      const data = await response.json();
      setBranches(data.branches || []);
      setCurrentBranch(data.current || 'main');
      setStashes(data.stashes || []);
      setHasStashes(data.hasStashes || false);
      
      return data;
    } catch (err) {
      console.error('[Branch] Fetch error:', err);
      return null;
    } finally {
      setBranchLoading(false);
    }
  }, [brandId]);

  /**
   * Create a new branch and switch to it
   */
  const createBranch = useCallback(async (branchName, fromRef = 'HEAD') => {
    if (!brandId || !branchName) return null;
    
    setBranchLoading(true);
    
    try {
      const response = await fetch(`${API_BASE}/api/brands/${brandId}/sandbox/git`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          action: 'create-branch',
          name: branchName,
          from: fromRef,
        }),
      });
      
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || `HTTP ${response.status}`);
      }
      
      const data = await response.json();
      
      // Refresh branch list
      await fetchBranches();
      
      return data;
    } catch (err) {
      console.error('[Branch] Create error:', err);
      throw err;
    } finally {
      setBranchLoading(false);
    }
  }, [brandId, fetchBranches]);

  /**
   * Switch to a different branch
   * @param {string} branchName - Branch to switch to
   * @param {Object} options - Options
   * @param {boolean} options.stash - If true, stash uncommitted changes before switching
   */
  const switchBranch = useCallback(async (branchName, options = {}) => {
    if (!brandId || !branchName) return null;
    
    setBranchLoading(true);
    
    try {
      const response = await fetch(`${API_BASE}/api/brands/${brandId}/sandbox/git`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          action: 'switch-branch',
          name: branchName,
          stash: options.stash || false,
        }),
      });
      
      const data = await response.json().catch(() => ({}));
      
      if (!response.ok) {
        // If there are uncommitted changes and user hasn't chosen to stash
        if (data.canStash && !options.stash) {
          // Offer to stash
          const shouldStash = window.confirm(
            `There are uncommitted changes:\n${data.changes?.map(c => c.path).join('\n') || 'Unknown files'}\n\nWould you like to stash these changes and switch branches?`
          );
          
          if (shouldStash) {
            // Retry with stash option
            return await switchBranch(branchName, { stash: true });
          }
        }
        throw new Error(data.error || `HTTP ${response.status}`);
      }
      
      setCurrentBranch(branchName);
      
      // Refresh branch list to get updated stash info
      await fetchBranches();
      
      // Check if we stashed changes and notify user
      if (options.stash) {
        console.log('[Branch] Changes were stashed. You can restore them using "Apply Stash" in the branch menu.');
      }
      
      // Restart dev server after branch switch to avoid stale cache
      setTimeout(() => {
        restartDevServer();
      }, 100);
      
      // Return data with stash indicator
      return { ...data, wasStashed: !!options.stash };
    } catch (err) {
      console.error('[Branch] Switch error:', err);
      throw err;
    } finally {
      setBranchLoading(false);
    }
  }, [brandId, restartDevServer, fetchBranches]);

  /**
   * Merge a branch into the current branch (or target)
   */
  const mergeBranch = useCallback(async (sourceBranch, targetBranch = null) => {
    if (!brandId || !sourceBranch) return null;
    
    setBranchLoading(true);
    
    try {
      const response = await fetch(`${API_BASE}/api/brands/${brandId}/sandbox/git`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          action: 'merge-branch',
          source: sourceBranch,
          target: targetBranch,
        }),
      });
      
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        if (data.conflicts) {
          throw new Error('Merge conflict detected. Merge aborted.');
        }
        throw new Error(data.error || `HTTP ${response.status}`);
      }
      
      const data = await response.json();
      
      // Refresh branch list
      await fetchBranches();
      
      // Restart dev server after merge to avoid stale cache
      setTimeout(() => {
        restartDevServer();
      }, 100);
      
      return data;
    } catch (err) {
      console.error('[Branch] Merge error:', err);
      throw err;
    } finally {
      setBranchLoading(false);
    }
  }, [brandId, fetchBranches, restartDevServer]);

  /**
   * Delete a branch
   */
  const deleteBranch = useCallback(async (branchName, force = false) => {
    if (!brandId || !branchName) return null;
    
    setBranchLoading(true);
    
    try {
      const response = await fetch(`${API_BASE}/api/brands/${brandId}/sandbox/git`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          action: 'delete-branch',
          name: branchName,
          force,
        }),
      });
      
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || `HTTP ${response.status}`);
      }
      
      const data = await response.json();
      
      // Refresh branch list
      await fetchBranches();
      
      return data;
    } catch (err) {
      console.error('[Branch] Delete error:', err);
      throw err;
    } finally {
      setBranchLoading(false);
    }
  }, [brandId, fetchBranches]);

  /**
   * Pop the most recent stash (apply and remove)
   */
  const popStash = useCallback(async () => {
    if (!brandId) return null;
    
    setBranchLoading(true);
    
    try {
      const response = await fetch(`${API_BASE}/api/brands/${brandId}/sandbox/git`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          action: 'stash-pop',
        }),
      });
      
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || `HTTP ${response.status}`);
      }
      
      const data = await response.json();
      
      // Refresh branch list to update stash count
      await fetchBranches();
      
      return data;
    } catch (err) {
      console.error('[Stash] Pop error:', err);
      throw err;
    } finally {
      setBranchLoading(false);
    }
  }, [brandId, fetchBranches]);

  /**
   * Drop a stash without applying
   */
  const dropStash = useCallback(async (stashRef = null) => {
    if (!brandId) return null;
    
    setBranchLoading(true);
    
    try {
      const response = await fetch(`${API_BASE}/api/brands/${brandId}/sandbox/git`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          action: 'stash-drop',
          ref: stashRef,
        }),
      });
      
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || `HTTP ${response.status}`);
      }
      
      const data = await response.json();
      
      // Refresh branch list to update stash count
      await fetchBranches();
      
      return data;
    } catch (err) {
      console.error('[Stash] Drop error:', err);
      throw err;
    } finally {
      setBranchLoading(false);
    }
  }, [brandId, fetchBranches]);

  /**
   * Apply a stash without removing it
   */
  const applyStash = useCallback(async (stashRef = null) => {
    if (!brandId) return null;
    
    setBranchLoading(true);
    
    try {
      const response = await fetch(`${API_BASE}/api/brands/${brandId}/sandbox/git`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          action: 'stash-apply',
          ref: stashRef,
        }),
      });
      
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || `HTTP ${response.status}`);
      }
      
      return await response.json();
    } catch (err) {
      console.error('[Stash] Apply error:', err);
      throw err;
    } finally {
      setBranchLoading(false);
    }
  }, [brandId]);

  /**
   * Compare two branches - get ahead/behind counts and diff
   */
  const compareBranches = useCallback(async (baseBranch, compareBranch) => {
    if (!brandId || !baseBranch || !compareBranch) return null;
    
    try {
      const response = await fetch(
        `${API_BASE}/api/brands/${brandId}/sandbox/git?action=compare-branches&base=${encodeURIComponent(baseBranch)}&compare=${encodeURIComponent(compareBranch)}`,
        { credentials: 'include' }
      );
      
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || `HTTP ${response.status}`);
      }
      
      return await response.json();
    } catch (err) {
      console.error('[Branch] Compare error:', err);
      throw err;
    }
  }, [brandId]);

  /**
   * Cherry-pick a specific commit into current branch
   */
  const cherryPick = useCallback(async (commitHash) => {
    if (!brandId || !commitHash) return null;
    
    setBranchLoading(true);
    
    try {
      const response = await fetch(`${API_BASE}/api/brands/${brandId}/sandbox/git`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          action: 'cherry-pick',
          commit: commitHash,
        }),
      });
      
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || `HTTP ${response.status}`);
      }
      
      return await response.json();
    } catch (err) {
      console.error('[Branch] Cherry-pick error:', err);
      throw err;
    } finally {
      setBranchLoading(false);
    }
  }, [brandId]);

  /**
   * Pull changes from another branch into current (merge with options)
   * @param {string} sourceBranch - Branch to pull from
   * @param {Object} options
   * @param {string} options.strategy - 'merge' | 'squash' | 'no-ff'
   */
  const pullFromBranch = useCallback(async (sourceBranch, options = {}) => {
    if (!brandId || !sourceBranch) return null;
    
    setBranchLoading(true);
    
    try {
      const response = await fetch(`${API_BASE}/api/brands/${brandId}/sandbox/git`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          action: 'pull-branch',
          source: sourceBranch,
          strategy: options.strategy || 'merge',
        }),
      });
      
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || `HTTP ${response.status}`);
      }
      
      const data = await response.json();
      
      // Refresh branches
      await fetchBranches();
      
      // Restart dev server after pull to avoid stale cache
      setTimeout(() => {
        restartDevServer();
      }, 100);
      
      return data;
    } catch (err) {
      console.error('[Branch] Pull error:', err);
      throw err;
    } finally {
      setBranchLoading(false);
    }
  }, [brandId, fetchBranches, restartDevServer]);

  /**
   * Rebase current branch onto another
   */
  const rebaseBranch = useCallback(async (ontoBranch) => {
    if (!brandId || !ontoBranch) return null;
    
    setBranchLoading(true);
    
    try {
      const response = await fetch(`${API_BASE}/api/brands/${brandId}/sandbox/git`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          action: 'rebase',
          onto: ontoBranch,
        }),
      });
      
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || `HTTP ${response.status}`);
      }
      
      const data = await response.json();
      
      // Refresh branches
      await fetchBranches();
      
      return data;
    } catch (err) {
      console.error('[Branch] Rebase error:', err);
      throw err;
    } finally {
      setBranchLoading(false);
    }
  }, [brandId, fetchBranches]);

  /**
   * Get commits for a specific branch
   */
  const getBranchCommits = useCallback(async (branchName, limit = 20) => {
    if (!brandId) return null;
    
    try {
      const response = await fetch(
        `${API_BASE}/api/brands/${brandId}/sandbox/git?action=branch-commits&branch=${encodeURIComponent(branchName)}&limit=${limit}`,
        { credentials: 'include' }
      );
      
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || `HTTP ${response.status}`);
      }
      
      return await response.json();
    } catch (err) {
      console.error('[Branch] Get commits error:', err);
      throw err;
    }
  }, [brandId]);

  /**
   * Auto-init on mount if sandbox doesn't exist
   */
  useEffect(() => {
    if (!brandId || !brandSlug) return;
    if (initAttempted.current) return;
    
    initAttempted.current = true;
    
    const initialize = async () => {
      const info = await checkStatus();
      
      if (autoInit && (!info || !info.exists)) {
        console.log(`[useBrandSandbox] Sandbox not found for ${brandSlug}, initializing...`);
        const created = await initSandbox();
        
        if (created) {
          console.log(`[useBrandSandbox] Sandbox created, syncing specs...`);
          await syncSpecs();
        }
      }
    };
    
    initialize();
  }, [brandId, brandSlug, autoInit, checkStatus, initSandbox, syncSpecs]);

  // Reset init flag when brand changes
  useEffect(() => {
    initAttempted.current = false;
    devServerStartAttempted.current = false;
  }, [brandId]);

  /**
   * Auto-start dev server when sandbox is ready
   */
  useEffect(() => {
    if (!autoStartDevServer) return;
    if (status !== SANDBOX_STATUS.READY) return;
    if (devServerStatus !== DEV_SERVER_STATUS.STOPPED) return;
    if (devServerStartAttempted.current) return;
    
    devServerStartAttempted.current = true;
    
    console.log(`[useBrandSandbox] Sandbox ready, starting dev server for ${brandSlug}...`);
    startDevServer();
  }, [autoStartDevServer, status, devServerStatus, brandSlug, startDevServer]);

  /**
   * Cleanup: stop dev server on unmount (optional - servers have auto-timeout)
   */
  useEffect(() => {
    return () => {
      // Note: We don't stop on unmount since the server has auto-idle timeout
      // This allows fast switching between views without restart delay
      // Uncomment below to stop on unmount:
      // if (devServerStatus === DEV_SERVER_STATUS.RUNNING) {
      //   stopDevServer();
      // }
    };
  }, [devServerStatus, stopDevServer]);

  /**
   * Get preview URL - prefer dev server, fallback to static
   */
  const previewUrl = devServerUrl 
    ? devServerUrl 
    : (brandId ? `${API_BASE}/api/brands/${brandId}/preview` : null);

  /**
   * Computed states
   */
  const isReady = status === SANDBOX_STATUS.READY;
  const isLoading = [
    SANDBOX_STATUS.CHECKING,
    SANDBOX_STATUS.INITIALIZING,
    SANDBOX_STATUS.SYNCING,
    SANDBOX_STATUS.GENERATING,
  ].includes(status);
  const needsInit = status === SANDBOX_STATUS.NOT_FOUND;
  const needsSync = status === SANDBOX_STATUS.STALE;
  const hasError = status === SANDBOX_STATUS.ERROR;
  
  // Dev server computed states
  const devServerRunning = devServerStatus === DEV_SERVER_STATUS.RUNNING;
  const devServerStarting = devServerStatus === DEV_SERVER_STATUS.STARTING;
  const devServerHasError = devServerStatus === DEV_SERVER_STATUS.ERROR;

  return {
    // Sandbox state
    status,
    sandboxInfo,
    error,
    lastSync,
    lastGenerate,
    previewUrl,
    
    // Dev server state
    devServerStatus,
    devServerUrl,
    devServerPort,
    devServerError,
    devServerRunning,
    devServerStarting,
    devServerHasError,
    
    // Branch state
    currentBranch,
    branches,
    branchLoading,
    stashes,
    hasStashes,
    
    // Computed
    isReady,
    isLoading,
    needsInit,
    needsSync,
    hasError,
    
    // Actions
    checkStatus,
    initSandbox,
    syncSpecs,
    syncSpecToDatabase,
    regenerate,
    resetSandbox,
    startDevServer,
    stopDevServer,
    restartDevServer,
    checkDevServerStatus,
    
    // Branch actions
    fetchBranches,
    createBranch,
    switchBranch,
    mergeBranch,
    deleteBranch,
    compareBranches,
    pullFromBranch,
    rebaseBranch,
    cherryPick,
    getBranchCommits,
    
    // Stash actions
    popStash,
    dropStash,
    applyStash,
  };
}

export default useBrandSandbox;

